(() => {
  const HOLDER_SELECTOR = '[id="CartDrawerRecommendations"]';
  const LIMIT = 12;
  const RECS_URL = '/recommendations/products';           // hard-code for reliability
  const FALLBACK_COLL = 'ymi-best-sellers';
  
  // Vendor-specific collection mappings
  const VENDOR_COLLECTIONS = {
    'YMI': ['ymi-best-sellers', 'ymi-all-jeans', 'ymi-menu-new'],
    'GIRLS': ['girls', 'ymi-girls-spring', 'girls-pants', 'girls-shorts-and-skirts'],
    'YMI GIRLS': ['ymi-girls-spring', 'girls', 'ymi-girls-cargo'],
    // Handle alternative vendor names
    'YMI JEANS': ['ymi-best-sellers', 'ymi-all-jeans'],
    'ROYALTYFORME': ['ymi-best-sellers'], // Fallback to YMI for RFM
    'DEFAULT': ['ymi-best-sellers'] // Ultimate fallback
  };
  const RETRY_DELAY = 200;                                // ms between cart.js retries
  const MAX_RETRIES = 6;

  const buildRecUrl = pid =>
    `${RECS_URL}?product_id=${pid}&limit=${LIMIT}&intent=related&section_id=product-recommendations`;

  const formatPrice = m =>
    (Number(m) > 1000 ? m / 100 : Number(m))
      .toLocaleString('en-US', { style: 'currency', currency: 'USD' });

  // Get unique vendors from cart items
  const getCartVendors = async () => {
    try {
      const cart = await fetch('/cart.js').then(r => r.json());
      const vendors = new Set();
      
      if (cart.items && cart.items.length > 0) {
        cart.items.forEach(item => {
          if (item.vendor) {
            const vendor = item.vendor.toUpperCase().trim();
            vendors.add(vendor);
          }
        });
      }
      
      return Array.from(vendors);
    } catch {
      return [];
    }
  };

  // Determine fallback collection based on cart vendors
  const getVendorBasedCollection = (vendors) => {
    if (!vendors || vendors.length === 0) {
      return VENDOR_COLLECTIONS['DEFAULT'][0];
    }

    // Check for exact matches first
    for (const vendor of vendors) {
      if (VENDOR_COLLECTIONS[vendor]) {
        return VENDOR_COLLECTIONS[vendor][0];
      }
    }

    // Check for partial matches - YMI GIRLS takes priority
    const hasYMIGirls = vendors.some(v => v.includes('YMI') && v.includes('GIRLS'));
    if (hasYMIGirls) {
      return VENDOR_COLLECTIONS['YMI GIRLS'][0];
    }

    // Check for combined YMI and GIRLS vendors (separate products)
    const hasYMI = vendors.some(v => v.includes('YMI'));
    const hasGirls = vendors.some(v => v.includes('GIRLS') || v.includes('GIRL'));
    
    if (hasYMI && hasGirls) {
      // Show YMI Girls collection when both types are in cart
      return VENDOR_COLLECTIONS['YMI GIRLS'][0];
    }

    // Check for YMI vendor variations
    if (hasYMI || vendors.some(v => v.includes('JEANS'))) {
      return VENDOR_COLLECTIONS['YMI'][0];
    }

    // Check for GIRLS vendor
    if (hasGirls) {
      return VENDOR_COLLECTIONS['GIRLS'][0];
    }

    // Check for RoyaltyForMe
    if (vendors.some(v => v.includes('ROYALTY') || v.includes('RFM'))) {
      return VENDOR_COLLECTIONS['ROYALTYFORME'][0];
    }

    // Ultimate fallback
    return VENDOR_COLLECTIONS['DEFAULT'][0];
  };

  const productCard = p => {
    const img = p.images?.[0]?.src
      ? p.images[0].src.replace(/(\.[a-z]+)(\?.*)?$/, '_400x$1')
      : '//cdn.shopify.com/s/assets/no-image-400x.gif';
    return `
      <div class="grid__item grid-product small--one-half medium-up--one-quarter"
           data-product-id="${p.id}" data-product-handle="${p.handle}">
        <a href="/products/${p.handle}" class="grid-product__link">
          <div class="grid-product__image-mask">
           
            <div class="image-wrap"><img src="${img}" alt="${p.title}" loading="lazy"></div>
          </div>
        </a>
        <div class="grid-product__meta">
          <div class="grid-product__title">${p.title}</div>
          <div class="grid-product__price">${formatPrice(p.variants[0].price)}</div>
        </div>
      </div>`;
  };

  const fetchFallback = async () => {
    // Get vendors from cart and determine appropriate collection
    const vendors = await getCartVendors();
    const collection = getVendorBasedCollection(vendors);
    
    const r = await fetch(`/collections/${collection}/products.json?limit=${LIMIT}`).then(x => x.json());
    const products = r.products || [];
    return { html: products.map(productCard).join(''), products };
  };

  const renderQuickShopModals = products => {
    const wrap = document.createElement('div');
    products.forEach(p => {
      wrap.innerHTML += `<div id="QuickShopModal-${p.id}" class="modal modal--square modal--quick-shop" data-product-id="${p.id}">
        <div class="modal__inner">
          <div class="modal__centered">
            <div class="modal__centered-content">
              <button type="button" class="modal__close js-modal-close text-link desktop-close-only">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 16 16">
                  <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="m3.334 3.333 9.333 9.334M12.667 3.333l-9.333 9.334"></path>
                </svg>
                <span>Close</span>
              </button>
              <div id="QuickShopHolder-${p.handle}"></div>
            </div>
          </div>
        </div>
      </div>`;
    });
    document.body.appendChild(wrap);
  };

  const injectBlock = (holder, title, markup, products = []) => {
    holder.innerHTML = `<div class="header-part">${title}</div>
                        <div class="product-part" data-event="open">${markup}</div>`;
    if (products.length) renderQuickShopModals(products);
    theme?.initQuickShop?.();
  };

  const extractProductsFromSwiper = (html) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;

    // Check if content has swiper slides
    const swiperSlides = tmp.querySelectorAll('.swiper-slide');
    if (swiperSlides.length > 0) {
      // Extract grid products from swiper slides
      const products = [];
      const productObjects = [];
      swiperSlides.forEach(slide => {
        const gridProduct = slide.querySelector('.grid__item.grid-product');
        if (gridProduct) {
          // Ensure consistent grid classes for all pages
          gridProduct.className = 'grid__item grid-product small--one-half medium-up--one-quarter';
          products.push(gridProduct.outerHTML);

          // Extract product data for quick shop modals
          const productId = gridProduct.getAttribute('data-product-id');
          const productHandle = gridProduct.getAttribute('data-product-handle');
          if (productId && productHandle) {
            productObjects.push({ id: productId, handle: productHandle });
          }
        }
      });
      return { html: products.join(''), products: productObjects };
    }

    // If no swiper slides, check for direct grid products
    const gridProducts = tmp.querySelectorAll('.grid__item.grid-product');
    if (gridProducts.length > 0) {
      const products = [];
      const productObjects = [];
      Array.from(gridProducts).forEach(product => {
        // Ensure consistent grid classes for all pages
        product.className = 'grid__item grid-product small--one-half medium-up--one-quarter';
        products.push(product.outerHTML);

        // Extract product data for quick shop modals
        const productId = product.getAttribute('data-product-id');
        const productHandle = product.getAttribute('data-product-handle');
        if (productId && productHandle) {
          productObjects.push({ id: productId, handle: productHandle });
        }
      });
      return { html: products.join(''), products: productObjects };
    }

    return { html: '', products: [] };
  };

  const renderHolder = async (holder, pid) => {
    
    
    holder.innerHTML = '';
    
    
    // ✅ Get upsell + fallback collection from Liquid attributes
    const upsellCollection = holder.dataset.upsellCollection;
    const fallbackCollection = holder.dataset.fallbackCollection;
    
    // If Upsell Collection is defined → always use that
    if (upsellCollection && upsellCollection.trim() !== '') {
      const r = await fetch(`/collections/${upsellCollection}/products.json?limit=${LIMIT}`).then(x => x.json());
      const products = r.products || [];
      if (products.length) {
        injectBlock(holder, 'Recommended For You', products.map(productCard).join(''), products);
        return; // stop here (don’t run recommendations)
      }
    }

    // If no upsell collection → run original recommendation logic
    const recUrl = pid ? buildRecUrl(pid) : null;
    const cartVendors = await getCartVendors();
    const hasVendorPreference = cartVendors.length > 0;

    if (recUrl) {
      try {
        const html = await fetch(recUrl).then(r => r.text());
        const tmp = document.createElement('div'); tmp.innerHTML = html;
        const recs = tmp.querySelector('.product-recommendations');
        if (recs && recs.innerHTML.trim()) {
          const { html: extractedHtml, products } = extractProductsFromSwiper(recs.innerHTML);
          if (extractedHtml) {
            injectBlock(holder, 'Frequently Bought Together', extractedHtml, products);
            return;
          }
        }
      } catch {/* fall through */}
    }

    // Recommendations returned nothing -> try theme Fallback Collection (only now)
    if (fallbackCollection && fallbackCollection.trim() !== '') {
      try {
        const rf = await fetch(`/collections/${fallbackCollection}/products.json?limit=${LIMIT}`).then(x => x.json());
        const fallbackProducts = rf.products || [];
        
        if (fallbackProducts.length) {
          const title = hasVendorPreference ? 'Recommended for You' : 'Best Sellers';
          injectBlock(holder, 'Recommended for You', fallbackProducts.map(productCard).join(''), fallbackProducts);
          return;
        }
      } catch (e) {
        // if this fails, we continue to vendor fallback
      }
    }
  };

  const loadProductIdWithRetry = async () => {
    for (let i = 0; i < MAX_RETRIES; i++) {
      try {
        const cart = await fetch('/cart.js').then(r => r.json());
        if (cart.items?.[0]?.product_id) return cart.items[0].product_id;
      } catch { }
      await new Promise(res => setTimeout(res, RETRY_DELAY));
    }
    return null;
  };

  const renderAll = async () => {
    const holders = [...document.querySelectorAll(HOLDER_SELECTOR)];
    if (!holders.length) return;
    const pid = await loadProductIdWithRetry();
    holders.forEach(h => renderHolder(h, pid));
  };

  /* fire on drawer open */
  new MutationObserver(() => {
    if (document.body.classList.contains('drawer--is-open')) renderAll();
  }).observe(document.body, { attributes: true, attributeFilter: ['class'] });

  /* fire after any cart-affecting fetch */
  const origFetch = window.fetch;
  window.fetch = async (...a) => {
    const resp = await origFetch(...a);
    const url = typeof a[0] === 'string' ? a[0] : a[0].url;
    if (/\/cart\/(add|change|update)(\.js)?/i.test(url)) renderAll();
    return resp;
  };

  document.addEventListener('DOMContentLoaded', renderAll);
})();
